import { useState } from "react";
import { Save, Trash2, Tag, ArrowLeft } from "lucide-react";
import {
  useTenantPricingRule,
  useSaveTenantPricing,
  useDeleteTenantPricing,
  usePricingRule,
} from "@/hooks/useBilling";

interface PricingFormData {
  name: string;
  price_per_vcpu: number;
  price_per_gb_ram: number;
  price_per_gb_disk: number;
  tier_a_multiplier: number;
  tier_b_multiplier: number;
  tier_c_multiplier: number;
  price_network_100m: number;
  price_network_500m: number;
  price_network_1g: number;
  price_backup_snapshot: number;
  price_backup_full: number;
  price_backup_incremental: number;
}

const FIELD_LABELS: {
  key: keyof Omit<PricingFormData, "name">;
  label: string;
  hint: string;
}[] = [
  { key: "price_per_vcpu", label: "Price per vCPU", hint: "EUR/month per core" },
  { key: "price_per_gb_ram", label: "Price per GB RAM", hint: "EUR/month per GB" },
  { key: "price_per_gb_disk", label: "Price per GB Disk", hint: "EUR/month per GB" },
  { key: "tier_a_multiplier", label: "Tier A Multiplier", hint: "Standalone (default 1.0)" },
  { key: "tier_b_multiplier", label: "Tier B Multiplier", hint: "Active/Passive (default 1.5)" },
  { key: "tier_c_multiplier", label: "Tier C Multiplier", hint: "Active/Active (default 2.0)" },
  { key: "price_network_100m", label: "Network 100 Mbit/s", hint: "EUR/month" },
  { key: "price_network_500m", label: "Network 500 Mbit/s", hint: "EUR/month" },
  { key: "price_network_1g", label: "Network 1 Gbit/s", hint: "EUR/month" },
  { key: "price_backup_snapshot", label: "Snapshot Backup", hint: "EUR/month" },
  { key: "price_backup_full", label: "Full Backup", hint: "EUR/month" },
  { key: "price_backup_incremental", label: "Incremental Backup", hint: "EUR/month" },
];

interface TenantPricingEditorProps {
  tenantId: number;
  tenantName: string;
  onClose: () => void;
}

export function TenantPricingEditor({
  tenantId,
  tenantName,
  onClose,
}: TenantPricingEditorProps) {
  const { data: tenantRule, isLoading: tenantLoading } =
    useTenantPricingRule(tenantId);
  const { data: globalRule } = usePricingRule();
  const saveMutation = useSaveTenantPricing();
  const deleteMutation = useDeleteTenantPricing();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PricingFormData | null>(null);

  const hasCustomPricing = tenantRule != null;

  const startEditing = () => {
    const source = tenantRule ?? globalRule;
    if (!source) return;
    setForm({
      name: tenantRule ? tenantRule.name : `${tenantName} Custom`,
      price_per_vcpu: source.price_per_vcpu,
      price_per_gb_ram: source.price_per_gb_ram,
      price_per_gb_disk: source.price_per_gb_disk,
      tier_a_multiplier: source.tier_a_multiplier,
      tier_b_multiplier: source.tier_b_multiplier,
      tier_c_multiplier: source.tier_c_multiplier,
      price_network_100m: source.price_network_100m,
      price_network_500m: source.price_network_500m,
      price_network_1g: source.price_network_1g,
      price_backup_snapshot: source.price_backup_snapshot,
      price_backup_full: source.price_backup_full,
      price_backup_incremental: source.price_backup_incremental,
    });
    setEditing(true);
  };

  const handleSave = () => {
    if (!form) return;
    saveMutation.mutate(
      { tenantId, payload: form },
      {
        onSuccess: () => setEditing(false),
      }
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(tenantId, {
      onSuccess: () => setEditing(false),
    });
  };

  const updateField = (key: keyof PricingFormData, value: string) => {
    if (!form) return;
    if (key === "name") {
      setForm({ ...form, name: value });
    } else {
      setForm({ ...form, [key]: parseFloat(value) || 0 });
    }
  };

  if (tenantLoading) return null;

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <Tag className="h-5 w-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-200">
            Pricing — {tenantName}
          </h2>
          <span
            className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${
              hasCustomPricing
                ? "bg-blue-500/20 text-blue-400"
                : "bg-gray-700 text-gray-400"
            }`}
          >
            {hasCustomPricing ? "Custom" : "Global"}
          </span>
        </div>
        {!editing && (
          <div className="flex gap-2">
            <button
              onClick={startEditing}
              disabled={!globalRule}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:bg-gray-800 disabled:opacity-50"
            >
              {hasCustomPricing ? "Edit Custom Pricing" : "Customize Pricing"}
            </button>
            {hasCustomPricing && (
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-800/50 px-3 py-1.5 text-sm text-red-400 transition-colors hover:bg-red-900/20 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Revert to Global
              </button>
            )}
          </div>
        )}
      </div>

      {editing && form ? (
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Rule Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              className="w-full max-w-xs rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Price fields grid */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {FIELD_LABELS.map(({ key, label, hint }) => (
              <div key={key}>
                <label className="mb-1 block text-sm text-gray-400">
                  {label}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form[key]}
                  onChange={(e) => updateField(key, e.target.value)}
                  className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                />
                <div className="mt-0.5 flex items-center gap-2">
                  <p className="text-xs text-gray-500">{hint}</p>
                  {globalRule && (
                    <p className="text-xs text-gray-600">
                      (global:{" "}
                      {typeof globalRule[key] === "number"
                        ? globalRule[key].toFixed(2)
                        : globalRule[key]}
                      )
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? "Saving..." : "Save Custom Pricing"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>

          {saveMutation.isError && (
            <p className="text-sm text-red-400">
              Failed to save: {saveMutation.error.message}
            </p>
          )}
        </div>
      ) : (
        /* Read-only view */
        <div>
          {hasCustomPricing && tenantRule ? (
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm md:grid-cols-3">
              <div>
                <span className="text-gray-500">Rule</span>
                <p className="font-medium text-gray-200">{tenantRule.name}</p>
              </div>
              {FIELD_LABELS.map(({ key, label }) => (
                <div key={key}>
                  <span className="text-gray-500">{label}</span>
                  <div className="flex items-baseline gap-2">
                    <p className="font-mono text-gray-200">
                      {typeof tenantRule[key] === "number"
                        ? tenantRule[key].toFixed(2)
                        : tenantRule[key]}
                    </p>
                    {globalRule && tenantRule[key] !== globalRule[key] && (
                      <span className="text-xs text-gray-600 line-through">
                        {typeof globalRule[key] === "number"
                          ? globalRule[key].toFixed(2)
                          : globalRule[key]}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <div>
                <span className="text-gray-500">Active since</span>
                <p className="text-gray-200">
                  {new Date(tenantRule.effective_from).toLocaleDateString("fr-FR")}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              This tenant uses the global pricing rule.
              {globalRule && (
                <span className="text-gray-500">
                  {" "}
                  ({globalRule.name} — {globalRule.price_per_vcpu.toFixed(2)}{" "}
                  EUR/vCPU, {globalRule.price_per_gb_ram.toFixed(2)} EUR/GB RAM)
                </span>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
